// @mui
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography, CircularProgress } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState, useRef } from 'react';
// components
import Iconify from 'src/components/iconify';
import { getRequest, postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import useSectorSection from 'src/hooks/use-sector-page';

// ----------------------------------------------------------------------

export default function Sector() {
   const { enqueueSnackbar } = useSnackbar();
  const { fetchOnMount } = useSectorSection();
  const sectorPageData = useSelector((state) => state.website.sectorPage);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    // Main Header
    mainTitle: "",
    mainTitleColor: "#000000",
    subtitle: "",
    subtitleColor: "#000000",

    // Main Description
    mainDescription: "",
    mainDescriptionColor: "#555555",

    // All-in-One Collaboration Section
    collaborationTitle: "",
    collaborationTitleColor: "#000000",
    collaborationBackgroundColor: "#f8f9fa",
    collaborationBorderColor: "#e9ecef",

    collaborationPoints: [
      {
        // id: 'broad-stakeholder',
        text: "",
        textColor: "#555555"
      },
      {
        // id: 'comprehensive-tools',
        text: "",
        textColor: "#555555"
      },
      {
        // id: 'adaptable-sectors',
        text: "",
        textColor: "#555555"
      }
    ],

    // Specific Integrations Section
    integrationsTitle: "",
    integrationsTitleColor: "#000000",

    sectors: [
      { id: '', name: "", color: "#555555" },
    ],

    // Bottom Section
    bottomTitle: "",
    bottomTitleColor: "#000000",
    bottomDescription: "",
    bottomDescriptionColor: "#555555",

    // General Styling
    // containerBackgroundColor: "#ffffff",
    // borderRadius: 1
  });


    useEffect(() => {
        fetchOnMount();
      }, [fetchOnMount]);
      useEffect(() => {
        setFormData(prev => ({
          ...prev,
          ...sectorPageData?.sector_page
        }));
      }, [sectorPageData])

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (typeof current[key] !== 'object' || Array.isArray(current[key])) {
          current[key] = {};
        }
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  const updateCollaborationPoint = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      collaborationPoints: prev.collaborationPoints.map((point, i) =>
        i === index ? { ...point, [field]: value } : point
      )
    }));
  };

  const updateSector = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      sectors: prev.sectors.map((sector, i) =>
        i === index ? { ...sector, [field]: value } : sector
      )
    }));
  };

  const addCollaborationPoint = () => {
    const newPoint = {
      id: `point-${Date.now()}`,
      text: "",
      textColor: "#555555"
    };

    setFormData(prev => ({
      ...prev,
      collaborationPoints: [...prev.collaborationPoints, newPoint]
    }));
  };

  const removeCollaborationPoint = (index) => {
    setFormData(prev => ({
      ...prev,
      collaborationPoints: prev.collaborationPoints.filter((_, i) => i !== index)
    }));
  };

  const addSector = () => {
    const newSector = {
      id: `sector-${Date.now()}`,
      name: "",
      color: "#555555"
    };

    setFormData(prev => ({
      ...prev,
      sectors: [...prev.sectors, newSector]
    }));
  };

  const removeSector = (index) => {
    setFormData(prev => ({
      ...prev,
      sectors: prev.sectors.filter((_, i) => i !== index)
    }));
  };

  const moveSector = (index, direction) => {
    const newSectors = [...formData.sectors];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex >= 0 && targetIndex < newSectors.length) {
      [newSectors[index], newSectors[targetIndex]] = [newSectors[targetIndex], newSectors[index]];
      setFormData(prev => ({ ...prev, sectors: newSectors }));
    }
  };

  const handleSave = async () => {
 setLoading(true);

    try {
      // Create a FormData object for file uploads
      const formDataToSend = new FormData();

      // Append all form data
      Object.entries(formData).forEach(([key, value]) => {
        // Skip heroImage as we'll handle it separately
        if (key !== 'heroImage') {
          // Handle nested objects like primaryButton and secondaryButton
          if (typeof value === 'object' && value !== null && !(value instanceof File)) {
            formDataToSend.append(key, JSON.stringify(value));
          } else {
            formDataToSend.append(key, value);
          }
        }
      });

      // Append the hero image if it exists
      if (formData.heroImage) {
        formDataToSend.append('heroImage', formData.heroImage);
      }

      // Make sure heroImageUrl is included in the submission
      if (formData.heroImageUrl) {
        formDataToSend.append('heroImageUrl', formData.heroImageUrl);
      }

      const response = await postRequestForWebsite(
        endpoints.admin_dashboard.website.sector_page.sector_section,
        // formDataToSend,
        // dataForHeroSection,
        {
          attributes: {
            ...JSON.parse(JSON.stringify(sectorPageData)),
            sector_page: formData
          }
        },
        true
      );

      if (response.metadata?.success) {
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
          By Sector Page Management
        </Typography>

        {/* Main Header Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Main Header</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Main Title"
                value={formData.mainTitle}
                onChange={(e) => updateFormData('mainTitle', e.target.value)}
                placeholder="Enter main title"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Main Title Color"
                type="color"
                value={formData.mainTitleColor}
                onChange={(e) => updateFormData('mainTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Subtitle"
                value={formData.subtitle}
                onChange={(e) => updateFormData('subtitle', e.target.value)}
                placeholder="Enter subtitle"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Subtitle Color"
                type="color"
                value={formData.subtitleColor}
                onChange={(e) => updateFormData('subtitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Main Description"
                multiline
                rows={4}
                value={formData.mainDescription}
                onChange={(e) => updateFormData('mainDescription', e.target.value)}
                placeholder="Enter main description"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Description Color"
                type="color"
                value={formData.mainDescriptionColor}
                onChange={(e) => updateFormData('mainDescriptionColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* All-in-One Collaboration Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center">
              <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
              <Typography variant="h6">All-in-One Collaboration Section</Typography>
            </Stack>
            <Button
              variant="contained"
              color="success"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={addCollaborationPoint}
            >
              Add Collaboration Point
            </Button>
          </Stack>

          {/* Section Title and Styling */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Collaboration Section Title"
                value={formData.collaborationTitle}
                onChange={(e) => updateFormData('collaborationTitle', e.target.value)}
                placeholder="Enter collaboration title"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Title Color"
                type="color"
                value={formData.collaborationTitleColor}
                onChange={(e) => updateFormData('collaborationTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Background Color"
                type="color"
                value={formData.collaborationBackgroundColor}
                onChange={(e) => updateFormData('collaborationBackgroundColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Border Color"
                type="color"
                value={formData.collaborationBorderColor}
                onChange={(e) => updateFormData('collaborationBorderColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>

          {/* Collaboration Points */}
          {formData.collaborationPoints.map((point, index) => (
            <Paper
              key={point.id}
              variant="outlined"
              sx={{ p: 3, mb: 2, bgcolor: 'background.neutral' }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="primary">
                  Collaboration Point {index + 1}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => removeCollaborationPoint(index)}
                  startIcon={<Iconify icon="mingcute:close-line" />}
                >
                  Remove
                </Button>
              </Stack>

              <Grid container spacing={2}>
                {/* <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Point ID"
                    value={point.id}
                    onChange={(e) => updateCollaborationPoint(index, 'id', e.target.value)}
                  />
                </Grid> */}
                <Grid item xs={12} md={10}>
                  <TextField
                    fullWidth
                    label="Point Text"
                    multiline
                    rows={3}
                    value={point.text}
                    onChange={(e) => updateCollaborationPoint(index, 'text', e.target.value)}
                    placeholder="Enter collaboration point description"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Text Color"
                    type="color"
                    value={point.textColor}
                    onChange={(e) => updateCollaborationPoint(index, 'textColor', e.target.value)}
                    sx={{ '& input': { height: 20 } }}
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>

        {/* Specific Integrations Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center">
              <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
              <Typography variant="h6">Specific Integrations Section</Typography>
            </Stack>
            <Button
              variant="contained"
              color="success"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={addSector}
            >
              Add Sector
            </Button>
          </Stack>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Integrations Section Title"
                value={formData.integrationsTitle}
                onChange={(e) => updateFormData('integrationsTitle', e.target.value)}
                placeholder="Enter integrations title"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Title Color"
                type="color"
                value={formData.integrationsTitleColor}
                onChange={(e) => updateFormData('integrationsTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>

          {/* Sectors List */}
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Sectors List
          </Typography>

          <Grid container spacing={2}>
            {formData.sectors.map((sector, index) => (
              <Grid item xs={12} md={6} key={sector.id}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: 'background.neutral' }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="caption" color="primary">
                      Sector {index + 1}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      {/* <Button
                        size="small"
                        variant="outlined"
                        disabled={index === 0}
                        onClick={() => moveSector(index, 'up')}
                      >
                        ↑
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={index === formData.sectors.length - 1}
                        onClick={() => moveSector(index, 'down')}
                      >
                        ↓
                      </Button> */}
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => removeSector(index)}
                      >
                        ×
                      </Button>
                    </Stack>
                  </Stack>

                  <Grid container spacing={3} sx={{ mb: 0 , mt:1 }}>
                    <Grid item xs={12} md={10}>
                      {/* <TextField
                      fullWidth
                      label="Sector ID"
                      size="small"
                      value={sector.id}
                      onChange={(e) => updateSector(index, 'id', e.target.value)}
                    /> */}
                      <TextField
                        fullWidth
                        label="Sector Name"
                        size="small"
                        sx={{ mb: 1 }}
                        value={sector.name}
                        onChange={(e) => updateSector(index, 'name', e.target.value)}
                        placeholder="Enter sector name"
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                      fullWidth
                      label="Text Color"
                      type="color"
                      size="small"
                      value={sector.color}
                      onChange={(e) => updateSector(index, 'color', e.target.value)}
                      sx={{ '& input': { height: 20 } }}
                    />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Bottom Section */}
        <Box sx={{ mb: 0, pb: 3}}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Bottom Section</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Bottom Section Title"
                value={formData.bottomTitle}
                onChange={(e) => updateFormData('bottomTitle', e.target.value)}
                placeholder="Enter bottom title"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Title Color"
                type="color"
                value={formData.bottomTitleColor}
                onChange={(e) => updateFormData('bottomTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Bottom Description"
                multiline
                rows={3}
                value={formData.bottomDescription}
                onChange={(e) => updateFormData('bottomDescription', e.target.value)}
                placeholder="Enter bottom description"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Description Color"
                type="color"
                value={formData.bottomDescriptionColor}
                onChange={(e) => updateFormData('bottomDescriptionColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* General Styling */}
        {/* <Box sx={{ mb: 5 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">General Styling</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Container Background Color"
                type="color"
                value={formData.containerBackgroundColor}
                onChange={(e) => updateFormData('containerBackgroundColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Border Radius"
                type="number"
                value={formData.borderRadius}
                onChange={(e) => updateFormData('borderRadius', parseInt(e.target.value))}
                placeholder="Enter border radius"
              />
            </Grid>
          </Grid>
        </Box> */}

        {/* Action Buttons */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ pt: 3, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <LoadingButton
            loading={loading}
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<Iconify icon="eva:save-fill" />}
            size="large"
          >
            Update
          </LoadingButton>
        </Stack>
      </CardContent>
    </Card>
  );
}
